import { Button, ModeToggle } from "@fleetminder/ui";

import { TruckIcon } from "lucide-react";
import { type ComponentType, lazy, Suspense } from "react";
import {
  createBrowserRouter,
  Link,
  Outlet,
  type RouteObject,
  useRouteError,
} from "react-router-dom";

import {
  OrganizationLanding,
  RequireOrganizationMembership,
} from "./organization.js";
import { RedirectWhenSignedIn, RequireSession, useSession } from "./session.js";
import {
  AuthenticatedShell,
  GlobalLoading,
  NotFoundPage,
  RouteFailureBoundary,
  RouteGroupErrorPage,
} from "./shared-ui.js";

export const webRouteGroups = [
  "public",
  "authentication",
  "organization",
  "driver-token",
] as const;

export const cloudflareSpaFallbackRules = [
  "/assets/* /assets/:splat 404",
  "/* /index.html 200",
] as const;

type LazyModule<TName extends string> = Record<TName, ComponentType>;

function lazyRoute<TName extends string>(
  load: () => Promise<LazyModule<TName>>,
  exportName: TName,
): ComponentType {
  return lazy(async () => {
    const module = await load();

    return { default: module[exportName] };
  }) as unknown as ComponentType;
}

const PublicHome = lazyRoute(
  () => import("./route-groups/public-routes.js"),
  "PublicHome",
);
const InvitationAcceptanceRoute = lazyRoute(
  () => import("./route-groups/public-routes.js"),
  "InvitationAcceptanceRoute",
);
const SignInRoute = lazyRoute(
  () => import("./route-groups/authentication-routes.js"),
  "SignInRoute",
);
const SignUpRoute = lazyRoute(
  () => import("./route-groups/authentication-routes.js"),
  "SignUpRoute",
);
const VerifyEmailRoute = lazyRoute(
  () => import("./route-groups/authentication-routes.js"),
  "VerifyEmailRoute",
);
const VerificationCompleteRoute = lazyRoute(
  () => import("./route-groups/authentication-routes.js"),
  "VerificationCompleteRoute",
);
const RecoverAccountRoute = lazyRoute(
  () => import("./route-groups/authentication-routes.js"),
  "RecoverAccountRoute",
);
const ResetPasswordRoute = lazyRoute(
  () => import("./route-groups/authentication-routes.js"),
  "ResetPasswordRoute",
);
const AccountRoute = lazyRoute(
  () => import("./route-groups/account-routes.js"),
  "AccountRoute",
);
const OrganizationOverview = lazyRoute(
  () => import("./route-groups/organization-routes.js"),
  "OrganizationOverview",
);
const OrganizationActivity = lazyRoute(
  () => import("./route-groups/organization-routes.js"),
  "OrganizationActivity",
);
const OrganizationInvitations = lazyRoute(
  () => import("./route-groups/organization-routes.js"),
  "OrganizationInvitations",
);
const OrganizationOnboarding = lazyRoute(
  () => import("./route-groups/organization-routes.js"),
  "OrganizationOnboarding",
);
const OrganizationSelector = lazyRoute(
  () => import("./route-groups/organization-routes.js"),
  "OrganizationSelector",
);
const DriverCheckIn = lazyRoute(
  () => import("./route-groups/driver-token-routes.js"),
  "DriverCheckIn",
);
const DriverIssueReport = lazyRoute(
  () => import("./route-groups/driver-token-routes.js"),
  "DriverIssueReport",
);
const ComponentGallery = lazyRoute(
  () => import("./route-groups/component-gallery.js"),
  "ComponentGallery",
);
const AddVehicleRoute = lazyRoute(
  () => import("./route-groups/vehicle-routes.js"),
  "AddVehicleRoute",
);
const VehicleInventoryRoute = lazyRoute(
  () => import("./route-groups/vehicle-routes.js"),
  "VehicleInventoryRoute",
);
const VehicleProfileRoute = lazyRoute(
  () => import("./route-groups/vehicle-routes.js"),
  "VehicleProfileRoute",
);
const DriversRoute = lazyRoute(
  () => import("./route-groups/driver-routes.js"),
  "DriversRoute",
);
const FleetMaintenanceRoute = lazyRoute(
  () => import("./route-groups/fleet-maintenance-routes.js"),
  "FleetMaintenanceRoute",
);
const ServicesRoute = lazyRoute(
  () => import("./route-groups/service-routes.js"),
  "ServicesRoute",
);
const LogServiceRoute = lazyRoute(
  () => import("./route-groups/service-routes.js"),
  "LogServiceRoute",
);
const IssueQueueRoute = lazyRoute(
  () => import("./route-groups/issue-routes.js"),
  "IssueQueueRoute",
);
const MaintenanceTemplatesRoute = lazyRoute(
  () => import("./route-groups/maintenance-template-routes.js"),
  "MaintenanceTemplatesRoute",
);

function AppRoot() {
  return (
    <div className="min-h-screen bg-background">
      <Outlet />
    </div>
  );
}

function PublicShell() {
  const { isLoading, status } = useSession();

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-10 border-b bg-background/80 backdrop-blur">
        <div className="mx-auto flex h-14 w-full max-w-6xl items-center gap-3 px-4">
          <Link className="flex items-center gap-2 font-semibold" to="/">
            <span className="flex size-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <TruckIcon aria-hidden className="size-4" />
            </span>
            FleetMinder
          </Link>
          <nav
            aria-label="Authentication"
            className="ml-auto flex items-center gap-1"
          >
            {/*
              Offering "Sign in" and "Create account" to someone already
              signed in asks them to solve a problem they do not have, and
              "create account" invites a second one they definitely do not
              want. While the session is still resolving neither is shown,
              because guessing wrong for a moment is worse than a brief gap.
            */}
            {isLoading ? null : status === "authenticated" ? (
              <Button asChild size="sm">
                <Link to="/app">Go to your fleet</Link>
              </Button>
            ) : (
              <>
                <Button asChild size="sm" variant="ghost">
                  <Link to="/auth/sign-in">Sign in</Link>
                </Button>
                <Button asChild size="sm">
                  <Link to="/auth/sign-up">Create account</Link>
                </Button>
              </>
            )}
            <ModeToggle />
          </nav>
        </div>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}

function renderRoute(Component: ComponentType, routeGroup: string) {
  return (
    <RouteFailureBoundary routeGroup={routeGroup}>
      <Suspense fallback={<GlobalLoading />}>
        <Component />
      </Suspense>
    </RouteFailureBoundary>
  );
}

function RouterErrorBoundary() {
  const error = useRouteError();

  return <RouteGroupErrorPage error={error} routeGroup="application" />;
}

export const routes = [
  {
    element: <AppRoot />,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        element: <PublicShell />,
        errorElement: <RouteGroupErrorPage routeGroup="public" />,
        children: [
          {
            element: renderRoute(PublicHome, "public"),
            index: true,
          },
          {
            element: (
              <RedirectWhenSignedIn>
                {renderRoute(SignInRoute, "authentication")}
              </RedirectWhenSignedIn>
            ),
            path: "auth/sign-in",
          },
          {
            element: renderRoute(InvitationAcceptanceRoute, "public"),
            path: "invitations/accept",
          },
          {
            element: (
              <RedirectWhenSignedIn>
                {renderRoute(SignUpRoute, "authentication")}
              </RedirectWhenSignedIn>
            ),
            path: "auth/sign-up",
          },
          {
            element: renderRoute(VerifyEmailRoute, "authentication"),
            path: "auth/verify-email",
          },
          {
            element: renderRoute(VerificationCompleteRoute, "authentication"),
            path: "auth/verification-complete",
          },
          {
            element: renderRoute(RecoverAccountRoute, "authentication"),
            path: "auth/recover",
          },
          {
            element: renderRoute(ResetPasswordRoute, "authentication"),
            path: "auth/reset-password",
          },
        ],
      },
      {
        element: (
          <RequireSession>
            <AuthenticatedShell />
          </RequireSession>
        ),
        errorElement: <RouteGroupErrorPage routeGroup="organization" />,
        path: "app",
        children: [
          {
            element: <OrganizationLanding />,
            index: true,
          },
          {
            element: renderRoute(AccountRoute, "account"),
            handle: { breadcrumb: "Your account" },
            path: "account",
          },
          {
            element: renderRoute(OrganizationOnboarding, "organization"),
            handle: { breadcrumb: "Set up company" },
            path: "onboarding/organization",
          },
          {
            element: renderRoute(OrganizationSelector, "organization"),
            handle: { breadcrumb: "Companies" },
            path: "organizations/select",
          },
          {
            element: (
              <RequireOrganizationMembership>
                {renderRoute(OrganizationOverview, "organization")}
              </RequireOrganizationMembership>
            ),
            handle: { breadcrumb: "Dashboard" },
            path: "organizations/:organizationId",
          },
          {
            element: (
              <RequireOrganizationMembership>
                {renderRoute(OrganizationInvitations, "organization")}
              </RequireOrganizationMembership>
            ),
            handle: { breadcrumb: "Team" },
            path: "organizations/:organizationId/invitations",
          },
          {
            element: (
              <RequireOrganizationMembership>
                {renderRoute(OrganizationActivity, "organization")}
              </RequireOrganizationMembership>
            ),
            handle: { breadcrumb: "Activity" },
            path: "organizations/:organizationId/activity",
          },
          {
            element: (
              <RequireOrganizationMembership>
                {renderRoute(AddVehicleRoute, "organization")}
              </RequireOrganizationMembership>
            ),
            handle: { breadcrumb: "Add vehicle" },
            path: "organizations/:organizationId/vehicles/new",
          },
          {
            element: (
              <RequireOrganizationMembership>
                {renderRoute(VehicleInventoryRoute, "organization")}
              </RequireOrganizationMembership>
            ),
            handle: { breadcrumb: "Vehicles" },
            path: "organizations/:organizationId/vehicles",
          },
          {
            element: (
              <RequireOrganizationMembership>
                {renderRoute(DriversRoute, "organization")}
              </RequireOrganizationMembership>
            ),
            handle: { breadcrumb: "Drivers" },
            path: "organizations/:organizationId/drivers",
          },
          {
            element: (
              <RequireOrganizationMembership>
                {renderRoute(FleetMaintenanceRoute, "organization")}
              </RequireOrganizationMembership>
            ),
            handle: { breadcrumb: "Maintenance" },
            path: "organizations/:organizationId/maintenance",
          },
          {
            element: (
              <RequireOrganizationMembership>
                {renderRoute(MaintenanceTemplatesRoute, "organization")}
              </RequireOrganizationMembership>
            ),
            handle: { breadcrumb: "Maintenance plans" },
            path: "organizations/:organizationId/maintenance/templates",
          },
          {
            element: (
              <RequireOrganizationMembership>
                {renderRoute(IssueQueueRoute, "organization")}
              </RequireOrganizationMembership>
            ),
            handle: { breadcrumb: "Issues" },
            path: "organizations/:organizationId/issues",
          },
          {
            element: (
              <RequireOrganizationMembership>
                {renderRoute(ServicesRoute, "organization")}
              </RequireOrganizationMembership>
            ),
            handle: { breadcrumb: "Service history" },
            path: "organizations/:organizationId/services",
          },
          {
            element: (
              <RequireOrganizationMembership>
                {renderRoute(LogServiceRoute, "organization")}
              </RequireOrganizationMembership>
            ),
            handle: { breadcrumb: "Log service" },
            path: "organizations/:organizationId/services/new",
          },
          {
            element: (
              <RequireOrganizationMembership>
                {renderRoute(VehicleProfileRoute, "organization")}
              </RequireOrganizationMembership>
            ),
            handle: { breadcrumb: "Vehicle", vehicleBreadcrumb: true },
            path: "organizations/:organizationId/vehicles/:vehicleId",
          },
        ],
      },
      {
        errorElement: <RouteGroupErrorPage routeGroup="driver-token" />,
        path: "driver/:token",
        children: [
          {
            element: renderRoute(DriverCheckIn, "driver-token"),
            index: true,
          },
          {
            element: renderRoute(DriverIssueReport, "driver-token"),
            path: "issues",
          },
        ],
      },
      ...(typeof window !== "undefined" && import.meta.env.DEV
        ? [
            {
              element: renderRoute(ComponentGallery, "development"),
              path: "__gallery",
            },
          ]
        : []),
      {
        element: <NotFoundPage />,
        path: "*",
      },
    ],
  },
] satisfies RouteObject[];

export function createAppRouter() {
  return createBrowserRouter(routes);
}
